import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { StatusBadge } from '@truecare/ui';
import type {
  AdminHomecareListItem,
  AdminHomecareListResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import {
  AdminTable,
  type AdminTableColumn,
} from '../../components/AdminTable';
import {
  AdminListFilters,
  type FilterField,
} from '../../components/AdminListFilters';

/**
 * Phase 14b-2 — Homecare clients list (persona-tailored columns per
 * 14b-2-5; operational filters per 14b-2-4; row click navigates to
 * detail page per 14b-2-2 Stripe Dashboard pattern).
 *
 * Filters use existing backend query params (status + paymentMode);
 * no backend additions needed for homecare per the 14b-2 pre-build
 * sweep — `paymentMethodState` lock semantic maps to the existing
 * `hasStripeCustomer: boolean` field on the list-item shape.
 */

const SearchSchema = z.object({
  status: z.string().optional(),
  paymentMode: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
});

type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute('/homecare-clients/')({
  beforeLoad: () => adminBeforeLoad('/homecare-clients'),
  validateSearch: (search) => SearchSchema.parse(search),
  component: HomecareClientsListPage,
});

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'TERMINATED', label: 'Terminated' },
];

const PAYMENT_MODE_OPTIONS = [
  { value: 'PAYG', label: 'Pay-as-you-go' },
  { value: 'STRIPE', label: 'Stripe' },
];

const FILTER_FIELDS: FilterField[] = [
  { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
  {
    name: 'paymentMode',
    label: 'Payment mode',
    type: 'select',
    options: PAYMENT_MODE_OPTIONS,
  },
];

const COLUMNS: AdminTableColumn<AdminHomecareListItem>[] = [
  {
    key: 'displayName',
    header: 'Name',
    render: (r) => r.displayName ?? <span className="text-ink-muted">—</span>,
  },
  { key: 'email', header: 'Email' },
  {
    key: 'status',
    header: 'Status',
    width: 'w-32',
    render: (r) => <StatusBadge status={r.status} />,
  },
  { key: 'paymentMode', header: 'Payment', width: 'w-32' },
  {
    key: 'hasStripeCustomer',
    header: 'Card on file',
    sortable: false,
    width: 'w-28',
    render: (r) =>
      r.hasStripeCustomer ? (
        <span className="text-forest">Yes</span>
      ) : (
        <span className="text-ink-muted">No</span>
      ),
  },
  {
    key: 'createdAt',
    header: 'Signed up',
    width: 'w-36',
    render: (r) => new Date(r.createdAt).toLocaleDateString('en-CA'),
  },
];

function HomecareClientsListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const query = useQuery<AdminHomecareListResponse>({
    queryKey: ['admin', 'homecare-clients', search],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/homecare-clients', {
          params: {
            query: {
              status: search.status,
              paymentMode: search.paymentMode,
              page: search.page,
              pageSize: search.limit,
            },
          },
        } as never),
      )) as unknown as AdminHomecareListResponse,
  });

  function updateSearch(next: Partial<Search>): void {
    navigate({ search: { ...search, ...next } as Search });
  }

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-forest md:text-4xl">Homecare clients</h1>
      <p className="mt-1 text-sm text-ink-muted">
        People booking in-home care for themselves or a loved one. Browse and
        filter here; click a row to open their detail page and take action.
      </p>

      {/* AdminListFilters renders its own bordered bar — no outer Card wrapper
          (Phase 17 M5: avoid the box-in-box above the table). */}
      <div className="mt-6">
        <AdminListFilters
          fields={FILTER_FIELDS}
          values={{
            status: search.status,
            paymentMode: search.paymentMode,
          }}
          onChange={(next) =>
            updateSearch({ ...next, page: 1 } as Partial<Search>)
          }
        />
      </div>

      <div className="mt-6" data-testid="homecare-list-table">
        <AdminTable<AdminHomecareListItem>
          columns={COLUMNS}
          rows={query.data?.items ?? []}
          isLoading={query.isPending}
          isError={query.isError}
          emptyMessage="No homecare clients match these filters."
          page={search.page}
          limit={search.limit}
          totalCount={query.data?.totalCount ?? 0}
          onPageChange={(page) => updateSearch({ page })}
          onLimitChange={(limit) => updateSearch({ limit, page: 1 })}
          onRowClick={(row) =>
            navigate({ to: '/homecare-clients/$id', params: { id: row.id } })
          }
        />
      </div>
    </AdminLayout>
  );
}
